import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu as MenuIcon, X, ShoppingBag } from "lucide-react";
import { BUSINESS } from "../data/site";
import { useCart } from "../context/CartContext";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Menu", href: "#menu" },
  { label: "Contact", href: "#contact" },
];

export const scrollTo = (href) => {
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Logo = () => (
  <button
    data-testid="nav-logo"
    onClick={() => scrollTo("#home")}
    aria-label="Croudace Pizza & Burger — back to top"
    className="flex items-center gap-3"
  >
    <img
      src="/logo-badge.jpg"
      alt="Croudace Pizza & Burger logo"
      className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/60 md:h-12 md:w-12"
    />
    <span className="hidden flex-col items-start leading-none sm:flex">
      <span className="font-serif text-xl font-bold italic text-charcoal">Croudace</span>
      <span className="text-[0.55rem] font-semibold uppercase tracking-[0.3em] text-ember">Pizza &amp; Burger</span>
    </span>
  </button>
);

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  const cartButton = (
    <button
      data-testid="nav-cart-button"
      onClick={() => setCartOpen(true)}
      aria-label="Open your order cart"
      className="relative flex h-11 w-11 items-center justify-center rounded-full border border-charcoal/20 text-charcoal transition-colors hover:border-ember hover:text-ember"
    >
      <ShoppingBag size={18} />
      {count > 0 && (
        <motion.span
          key={count}
          initial={{ scale: 0.3 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 16 }}
          data-testid="nav-cart-count"
          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ember text-[0.65rem] font-bold text-cream"
        >
          {count}
        </motion.span>
      )}
    </button>
  );

  return (
    <header
      data-testid="main-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-cream/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <Logo dark />
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="nav-link text-sm font-medium uppercase tracking-widest text-charcoal/70 transition-colors hover:text-ember"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-order-button"
            href={BUSINESS.phoneHref}
            className="group flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105 hover:bg-[#991B1B]"
          >
            <Phone size={15} className="transition-transform duration-300 group-hover:rotate-12" />
            Call to Order
          </a>
          {cartButton}
        </nav>
        <div className="flex items-center gap-3 lg:hidden">
          {cartButton}
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="text-charcoal"
          >
            {open ? <X size={26} /> : <MenuIcon size={26} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-black/10 bg-cream/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-serif text-2xl font-semibold text-charcoal transition-colors hover:text-ember"
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                data-testid="nav-mobile-order-button"
                href={BUSINESS.phoneHref}
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-ember px-6 py-4 text-sm font-bold uppercase tracking-widest text-cream"
              >
                <Phone size={15} /> Call to Order — {BUSINESS.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
